// Single source of truth for /tools/cash-to-close.

export const PAGE_PATH = "/tools/cash-to-close";
export const PAGE_URL = "https://www.homeapproach.com/tools/cash-to-close";
export const PAGE_TITLE = "What Is Cash to Close and Why Does It Keep Changing?";
export const PAGE_DESCRIPTION =
  "Cash to close is not the same as closing costs. See the formula, a full line-item breakdown, why the number moves between the Loan Estimate and the Closing Disclosure, and how to verify it before you wire.";

/** Anchor sections used by the jump nav. */
export const SECTIONS = [
  { id: "vs-closing-costs", title: "Cash to close vs. closing costs" },
  { id: "formula", title: "The formula and every line item" },
  { id: "why-it-changes", title: "Why the number changes" },
  { id: "examples", title: "Three worked examples" },
  { id: "tolerances", title: "What can and can't change" },
  { id: "before-wiring", title: "What to do before wiring funds" },
  { id: "faq", title: "Common questions" },
];

export const COMPARISON = {
  heading: "Cash to close vs. closing costs — the 3-bullet version",
  bullets: [
    {
      label: "Closing costs",
      text: "The fees charged to complete the loan and the transfer: lender fees, title and escrow, recording, appraisal, and the prepaid items funded at settlement. On a typical purchase they run about 2–5% of the price.",
    },
    {
      label: "Cash to close",
      text: "Everything you actually bring to the table: closing costs plus your down payment, minus your earnest money deposit, seller credits, lender credits, and any assistance funds already applied.",
    },
    {
      label: "Why people mix them up",
      text: "Both appear on page 1 of the Loan Estimate and page 3 of the Closing Disclosure, side by side. Closing costs is a subtotal. Cash to close is the bottom line — it is always the larger number on a purchase because the down payment sits inside it.",
    },
  ],
};

export const FORMULA_LINE =
  "Down payment + closing costs + prepaids and escrow deposits − earnest money − seller credits − lender credits = cash to close";

export type FeeRow = {
  label: string;
  detail: string;
  amount: number;
  kind: "add" | "subtract" | "total";
};

/** Illustrative $350,000 purchase, 5% down, 6.5% rate, closing Aug 20. */
export const EXAMPLE_SCENARIO = {
  price: 350_000,
  downPct: 5,
  loanAmount: 332_500,
  rate: 6.5,
  perDiem: 59.21,
  prepaidDays: 12,
  closingDate: "August 20",
};

export const FEE_ROWS: FeeRow[] = [
  {
    label: "Down payment",
    detail: "5% of a $350,000 price. Set by your loan program, not by the lender's fees.",
    amount: 17_500,
    kind: "add",
  },
  {
    label: "Lender fees",
    detail: "Origination, underwriting, appraisal, and the credit report pull.",
    amount: 3_200,
    kind: "add",
  },
  {
    label: "Title and escrow",
    detail: "Lender's title policy, settlement or closing fee, and recording charges.",
    amount: 2_400,
    kind: "add",
  },
  {
    label: "Prepaid interest",
    detail: "12 days at $59.21 per day — interest from the closing date through the end of the month.",
    amount: 711,
    kind: "add",
  },
  {
    label: "Property tax escrow deposit",
    detail: "Six months of taxes at $350 per month, collected up front to seed the escrow account.",
    amount: 2_100,
    kind: "add",
  },
  {
    label: "Homeowners insurance",
    detail: "The first twelve months paid in full plus two months into escrow.",
    amount: 1_800,
    kind: "add",
  },
  {
    label: "Earnest money already paid",
    detail: "Wired to escrow when the offer was accepted. It is credited back to you at closing.",
    amount: -3_500,
    kind: "subtract",
  },
  {
    label: "Seller credit",
    detail: "None in this baseline. Any credit negotiated later reduces the number dollar for dollar.",
    amount: 0,
    kind: "subtract",
  },
  {
    label: "Estimated cash to close",
    detail: "What you would wire to the settlement agent on closing day.",
    amount: 24_211,
    kind: "total",
  },
];

export const TIMELINE = [
  {
    id: "prepaid-interest",
    stage: "Loan Estimate (day 3)",
    heading: "Prepaid interest is a guess until the date is locked",
    text: "Prepaid interest covers the closing date through the last day of the month. Every day the closing moves changes it by one day of interest — on a $332,500 loan at 6.5% that is about $59 a day.",
  },
  {
    id: "proration",
    stage: "Under contract",
    heading: "Property tax and HOA proration",
    text: "Taxes and HOA dues are split between you and the seller based on the exact closing date, and the escrow cushion is sized from the next real tax bill. A reassessment or a new bill issued mid-transaction moves the deposit.",
  },
  {
    id: "third-party",
    stage: "Two weeks out",
    heading: "Third-party invoices replace placeholders",
    text: "Survey, pest, HOA transfer, and municipal recording invoices arrive late. Until they do, the Loan Estimate carries the lender's best estimate rather than a real number.",
  },
  {
    id: "insurance-actuals",
    stage: "One week out",
    heading: "Insurance actuals land",
    text: "The lender estimates your homeowners premium early. When you bind a real policy, the actual annual premium and the escrow months are swapped in — up or down.",
  },
  {
    id: "earnest-money",
    stage: "Closing Disclosure (3 days out)",
    heading: "Earnest money and credits are verified",
    text: "The settlement agent confirms the deposit actually cleared and applies every seller and lender credit. This is usually the last line to change, and it moves the bottom line the most.",
  },
];

export type WorkedExample = {
  id: string;
  title: string;
  setup: string;
  math: { label: string; value: string }[];
  takeaway: string;
};

export const EXAMPLES: WorkedExample[] = [
  {
    id: "one-day-delay",
    title: "A one-day closing delay changes per diem interest",
    setup:
      "Closing slips from August 20 to August 21. Nothing else about the loan changes. Prepaid interest now covers 11 days instead of 12.",
    math: [
      { label: "Baseline cash to close", value: "$24,211" },
      { label: "Prepaid interest, 12 days at $59.21", value: "$711" },
      { label: "Prepaid interest, 11 days at $59.21", value: "$651" },
      { label: "Change", value: "−$60" },
      { label: "Revised cash to close", value: "$24,151" },
    ],
    takeaway:
      "Closing later in the month lowers prepaid interest; closing on the 1st maximizes it. This line moves on almost every file and is not a mistake.",
  },
  {
    id: "seller-credit",
    title: "A seller credit negotiated after inspection",
    setup:
      "The inspection turns up a failing water heater. Instead of repairing it, the seller agrees to a $4,000 credit toward your closing costs. Your total non-down-payment costs are $10,211, so the full credit is allowed.",
    math: [
      { label: "Baseline cash to close", value: "$24,211" },
      { label: "Seller credit applied", value: "−$4,000" },
      { label: "Revised cash to close", value: "$20,211" },
    ],
    takeaway:
      "A seller credit can only offset actual closing costs and prepaids — it cannot be applied to your down payment or handed to you as cash. Programs also cap credits by loan type and down payment size.",
  },
  {
    id: "insurance-actual",
    title: "An insurance quote replaces an estimate on the Closing Disclosure",
    setup:
      "The Loan Estimate assumed $1,800 for the first year plus escrow. Your bound policy comes in at $1,950 annually, so twelve months up front plus two months of escrow totals $2,340.",
    math: [
      { label: "Baseline cash to close", value: "$24,211" },
      { label: "Insurance estimated", value: "$1,800" },
      { label: "Insurance actual", value: "$2,340" },
      { label: "Change", value: "+$540" },
      { label: "Revised cash to close", value: "$24,751" },
    ],
    takeaway:
      "Insurance is one of the items with no tolerance limit because you choose the carrier. Shopping the policy earlier is the cheapest way to keep this line from surprising you.",
  },
];

export const TOLERANCES = [
  {
    id: "zero",
    label: "Zero tolerance — cannot increase at all",
    items:
      "The lender's own origination charge, points you locked, fees for services you were not allowed to shop for, and transfer taxes. If any of these go up, the lender generally has to credit the difference back to you at closing.",
  },
  {
    id: "ten",
    label: "10 percent cumulative tolerance",
    items:
      "Recording fees and services you shopped for using a provider from the lender's written list. Individual line items can move, but the group total generally cannot exceed the estimate by more than 10 percent.",
  },
  {
    id: "none",
    label: "No tolerance limit — expected to change",
    items:
      "Prepaid interest, homeowners insurance premiums, property tax and insurance escrow deposits, and any service you shopped for outside the lender's list. These are real third-party costs driven by your date and your choices, not by lender pricing.",
  },
];

export const WIRE_WARNING = {
  heading: "Before you send a single dollar: verify by phone",
  points: [
    "Wire fraud in real estate works by emailing you fake instructions that look identical to your title company's. Assume any emailed wire instruction is fraudulent until you prove otherwise.",
    "Call the settlement agent on a number you found yourself — from the signed contract or the company's website, never from the email — and read the account and routing numbers back to them.",
    "Be suspicious of any last-minute change to wiring instructions, urgency about a deadline, or a request to send funds to a different bank or a personal account.",
    "Ask your bank about recall procedures before you send, and call the settlement agent to confirm the funds arrived.",
  ],
};

export const BEFORE_WIRING = [
  "Read the Closing Disclosure the day it arrives — you are entitled to it at least three business days before closing, and that window exists so you can find errors.",
  "Put the Closing Disclosure next to your most recent Loan Estimate and compare page 1 and page 3 line by line. Circle anything that moved.",
  "Confirm every credit you were promised actually appears: seller credit, lender credit, earnest money, and any down payment assistance funds.",
  "Check the exact cash-to-close figure the settlement agent will accept, and confirm the acceptable form — most closings require a wire or a cashier's check, not a personal check.",
  "Ask your loan officer to walk you through any line that changed and which tolerance category it falls in.",
  "Verify wire instructions by phone, using a number you sourced independently.",
];

export const CTA = {
  label: "Ask a loan officer to review my cash-to-close line items.",
  to: "/lender-match",
};

export const FAQS = [
  {
    id: "why-changes",
    q: "Why does my cash to close keep changing?",
    a: "Most of the movement comes from four places: prepaid interest tied to the exact closing date, property tax and HOA proration, third-party invoices that replace early placeholders, and your actual insurance premium replacing the lender's estimate. Credits verified at the end — earnest money, seller credits, assistance funds — cause the last shift. Changes in these categories are normal; changes in the lender's own fees usually are not.",
  },
  {
    id: "vs-closing-costs",
    q: "How is cash to close different from closing costs?",
    a: "Closing costs are the fees to complete the loan and the transfer, typically 2–5% of the price. Cash to close is the full amount you bring to settlement: closing costs plus your down payment and prepaid escrow, minus earnest money and any seller or lender credits. On a purchase, cash to close is almost always the bigger number.",
  },
  {
    id: "can-it-go-up",
    q: "Can my cash to close go up after I get the Closing Disclosure?",
    a: "It can, though the amount is usually small. A closing date that moves changes prepaid interest, and a late invoice or a corrected proration can adjust the total. If the change is significant or affects your APR, loan product, or adds a prepayment penalty, the lender must issue a corrected Closing Disclosure and a new three-business-day waiting period starts.",
  },
  {
    id: "reduce",
    q: "How can I lower my cash to close?",
    a: "Negotiate a seller credit toward closing costs, ask about lender credits in exchange for a slightly higher rate, close later in the month to cut prepaid interest, shop your homeowners insurance and your title or settlement provider, and check whether you qualify for down payment or closing cost assistance in your area.",
  },
  {
    id: "how-to-pay",
    q: "How do I actually pay the cash to close?",
    a: "Nearly every settlement agent requires a wire transfer or a cashier's check; personal checks are typically not accepted for large amounts. Confirm the exact figure and the accepted method with the settlement agent, and always verify wire instructions by calling a phone number you looked up yourself rather than one sent by email.",
  },
];

export const DISCLAIMER =
  "This page is educational information, not lending, tax, or legal advice, and it is not a loan offer, pre-qualification, or commitment to lend. The figures shown are illustrative examples only; your actual cash to close depends on your price, loan program, location, closing date, and the terms in your contract. Tolerance rules are summarized in plain English and their application depends on your specific disclosures. Confirm your numbers with your licensed loan officer and settlement agent.";

const SITE = "https://www.homeapproach.com";

/** WebPage at the page level + BreadcrumbList + FAQPage, baked into server HTML. */
export const CASH_TO_CLOSE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
